import React, { useState } from "react";
import { Sketch } from "@uiw/react-color";
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

type ColorPickerProps = {
    color: string;
    onChange: (newColor: string) => void;
    title?: string;
    disabled?: boolean;
};

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, title, disabled = false}) => {
    const [displayPicker, setDisplayPicker] = useState(false);

    const isEyedropperSupported = "EyeDropper" in window;

    const handleEyedropper = async () => {
        if (isEyedropperSupported) {
            const eyeDropper = new (window as any).EyeDropper();
            try {
                const result = await eyeDropper.open();
                onChange(result.sRGBHex);
            } catch {
                console.log("Color picking canceled");
            }
        } else {
            // Fallback: open the Sketch picker
            setDisplayPicker(true);
        }
    };

    return (
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {/* Color preview rectangle */}
            <Tooltip
                title={title || "Select color"}
                arrow
                placement="top"
                disableHoverListener={disabled}
                disableFocusListener={disabled}
                disableTouchListener={disabled}
            >
                <div
                    onClick={() => {
                        if (disabled) return;
                        setDisplayPicker(!displayPicker);
                    }}
                    style={{
                        width: 28,
                        height: 28,
                        backgroundColor: color,
                        border: "2px solid #ccc",
                        borderRadius: 4,
                        cursor: "pointer",
                        boxShadow: "0 0 1px rgba(0,0,0,0.3)",
                    }}
                />
            </Tooltip>

            {/* Eyedropper button */}

            <Tooltip
                title={
                    disabled
                        ? ""
                        : isEyedropperSupported
                            ? "Pick color from screen"
                            : "Eyedropper not supported, opens color picker"
                }
                arrow
                placement="top"
                disableHoverListener={disabled}
                disableFocusListener={disabled}
                disableTouchListener={disabled}
            >
                <IconButton
                    disabled={disabled}
                    onClick={handleEyedropper}
                    size="small"
                    sx={{
                        width: 18,
                        height: 18,
                        padding: 0,
                        borderRadius: 1,
                        backgroundColor: "#fff",
                    }}
                >
                    <img
                        src={`${process.env.PUBLIC_URL}/Eyedropper.svg`}
                        alt="Eyedropper"
                        style={{ width: 18, height: 18 }}
                    />
                </IconButton>
            </Tooltip>

            {/* Sketch color palette fallback */}
            {displayPicker && (
                <div style={{ position: "absolute", zIndex: 1000, top: "100%", left: 0, marginTop: 4 }}>
                    <div
                        style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0 }}
                        onClick={() => setDisplayPicker(false)}
                    />
                    <Sketch
                        color={color}
                        onChange={(newColor) => onChange(newColor.hex)}
                    />
                </div>
            )}
        </div>
    );
};
